import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  CheckCircle2,
  XCircle,
  Star,
  Plus,
  MapPin,
  HardHat,
  Sparkles,
  Crown,
} from "lucide-react";
import { toast } from "react-toastify";
import {
  getProperties,
  getCities,
  getDevelopers,
  getAmenities,
} from "../../../services/api";
import "./Dashboard.css";

function coverOf(p) {
  if (Array.isArray(p.images) && p.images.length) return p.images[0];
  return p.cover || p.image || "";
}

function isPublished(p) {
  const status = String(p.status || "").trim().toLowerCase();
  if (status) return status === "published";
  if (p.isActive !== undefined) return p.isActive === true || p.isActive === "true";
  if (p.active !== undefined) return p.active !== false;
  return true;
}

export default function Dashboard() {
  const [items, setItems] = useState([]);
  const [masters, setMasters] = useState({
    cities: 0,
    developers: 0,
    amenities: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const [properties, cities, developers, amenities] = await Promise.all([
          getProperties().catch((err) => {
            console.error(err);
            toast.error(err.message || "Failed to load properties");
            return [];
          }),
          getCities().catch(() => []),
          getDevelopers().catch(() => []),
          getAmenities().catch(() => []),
        ]);

        if (cancelled) return;

        setItems(Array.isArray(properties) ? properties : []);
        setMasters({
          cities: Array.isArray(cities) ? cities.length : 0,
          developers: Array.isArray(developers) ? developers.length : 0,
          amenities: Array.isArray(amenities) ? amenities.length : 0,
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const total = items.length;
  const published = items.filter(isPublished).length;
  const featured = items.filter((p) => p.featured === true || p.featured === "true").length;
  const luxury = items.filter((p) => p.luxurious === true || p.luxurious === "true").length;

  // newest first if createdAt exists
  // All published, newest first (no .slice limit)
  const publishedList = [...items]
    .filter(isPublished)
    .sort((a, b) => {
      const ta = new Date(a.createdAt || a.updatedAt || 0).getTime();
      const tb = new Date(b.createdAt || b.updatedAt || 0).getTime();
      return tb - ta;
    });

  return (
    <div className="db">
      <div className="db-head">
        <div>
          <div className="db-eyebrow">Overview</div>
          <h1 className="db-title">Dashboard</h1>
          <p className="db-sub">
            A live snapshot of your property inventory and master data.
          </p>
        </div>
        <Link to="/admin/add-property" className="db-cta">
          <Plus className="h-4 w-4" />
          New Property
        </Link>
      </div>

      {loading ? (
        <p className="db-empty" style={{ padding: "2rem 0" }}>
          Loading dashboard…
        </p>
      ) : (
        <>
          <div className="db-grid">
            <Stat icon={Building2} label="Total properties" value={total} />
            <Stat icon={CheckCircle2} label="Published" value={published} />
            <Stat icon={Star} label="Featured" value={featured} />
            <Stat icon={Crown} label="Most luxurious" value={luxury} />
          </div>

          <div className="db-grid-3">
            <Stat icon={MapPin} label="Cities" value={masters.cities} />
            <Stat icon={HardHat} label="Developers" value={masters.developers} />
            <Stat icon={Sparkles} label="Amenities" value={masters.amenities} />
          </div>

          <div className="db-panel">
            <div className="db-panel-head">
              <h2 className="db-panel-title">Published properties</h2>
              <Link to="/admin/property-list" className="db-panel-link">
                Manage all →
              </Link>
            </div>

            <ul className="db-list">
              {publishedList.map((p) => {
                const thumb = coverOf(p);
                const active = isPublished(p);

                return (
                  <li key={p.id} className="db-row">
                    {thumb ? (
                      <img src={thumb} alt={p.title || p.name || ""} />
                    ) : (
                      <div className="db-row-placeholder" />
                    )}

                    <div className="db-row-body">
                      <div className="db-row-title">{p.title ?? p.name ?? "—"}</div>
                      <div className="db-row-sub">
                        {(p.developer ?? p.builder) || "—"} ·{" "}
                        {p.locality ?? p.location ?? p.city ?? "—"}
                      </div>
                    </div>

                    <span
                      className={`db-pill ${active ? "db-pill--on" : "db-pill--off"}`}
                    >
                      {active ? (
                        <CheckCircle2 className="h-3.5 w-3.5" />
                      ) : (
                        <XCircle className="h-3.5 w-3.5" />
                      )}
                      {active ? "Published" : "Inactive"}
                    </span>
                  </li>
                );
              })}

              {publishedList.length === 0 && (
                <li className="db-empty">No published properties yet.</li>
              )}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="db-stat">
      <div className="db-stat-ico">
        <Icon className="h-5 w-5" />
      </div>
      <div className="db-stat-label">{label}</div>
      <div className="db-stat-value">{value}</div>
    </div>
  );
}